// src/features/patient/care-treatment/utils/careTeamPlans.ts

/**
 * Purpose : The Care team and Care plans. The server builds both; this file only falls back for an older
 * server and applies the same rules: the team comes only from visit providers
 * and note authors, one card per person, grouped Doctors / Nurses & medical
 * assistants / Other; a care plan is real only with a title and a goal or
 * activity, never an order set, panel, protocol or order status text.
 *
 * Contents :
 *
 * * function(roleFromName) : doctor / nurse / other from the credentials
 * * function(buildCareTeam) : the team from visits and notes (older server)
 * * const(TEAM_GROUPS) / function(groupCareTeam) / function(initials)
 * * function(isOrderText) / function(cleanCarePlans)
 */

import type {
  CarePlanOut, CareTeamMember, Encounter, Note,
} from "@/features/patient/care-treatment/api/careTimelineTypes";
import { personKey } from "@/features/patient/care-treatment/utils/visitDays";

const DOCTOR = /\b(MD|DO|MBBS)\b/;
// Nurse practitioners and midwives are nurses by training: their own group.
const NURSE = /\b(RN|LPN|LVN|MA|CMA|RMA|CNA|NP|APRN|FNP|CNM|BSN|MSN)\b/;

// function(roleFromName)
/** From the credentials after the comma ("…, MD"); no credentials → other. */
export function roleFromName(name: string): CareTeamMember["role"] {
  const creds = name.includes(",") ? name.slice(name.indexOf(",") + 1).toUpperCase() : "";
  if (DOCTOR.test(creds)) return "doctor";
  if (NURSE.test(creds)) return "nurse";
  return "other";
}

// function(buildCareTeam)
/**
 * The care team from visit providers and note authors only. One member per
 * grouping key, with the most complete spelling of the name.
 *
 * @param encounters - Visits and records.
 * @param notes - Notes.
 * @returns Members, by name.
 */
export function buildCareTeam(encounters: Encounter[], notes: Note[]): CareTeamMember[] {
  const people = new Map<string, CareTeamMember>();
  const meet = (name: string | undefined, key: string | undefined, when: string, place: string | undefined, org: string | undefined, wrote: boolean) => {
    const display = name?.trim();
    if (!display) return;
    const k = personKey(display, key);
    const day = when.slice(0, 10);
    const member = people.get(k) ?? { key: k, name: display, role: "other", departments: [], notes: 0 };
    if (display.length > member.name.length) member.name = display;
    if (place?.trim() && !member.departments.includes(place.trim())) member.departments.push(place.trim());
    if (!member.organisation && org?.trim()) member.organisation = org.trim();
    if (wrote) member.notes += 1;
    if (day && (!member.lastSeen || day > member.lastSeen)) member.lastSeen = day;
    people.set(k, member);
  };
  const byId = new Map(encounters.map((e) => [e.id, e]));
  for (const e of encounters) meet(e.practitioner, e.practitionerKey, e.start, e.department || e.specialty, e.facility, false);
  for (const n of notes) {
    const visit = n.encounterId ? byId.get(n.encounterId) : undefined;
    meet(n.author, n.authorKey, n.date, n.department || visit?.department, visit?.facility, true);
  }
  return [...people.values()]
    .map((m) => ({ ...m, role: roleFromName(m.name) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// const(TEAM_GROUPS)
export const TEAM_GROUPS: { role: CareTeamMember["role"]; label: string }[] = [
  { role: "doctor", label: "Doctors" },
  { role: "nurse", label: "Nurses & medical assistants" },
  { role: "other", label: "Other" },
];

// function(groupCareTeam)
/** Members per group (empty groups left out); within a group, most notes first. */
export function groupCareTeam(members: CareTeamMember[]): { label: string; members: CareTeamMember[] }[] {
  return TEAM_GROUPS
    .map(({ role, label }) => ({
      label,
      members: members.filter((m) => m.role === role).sort((a, b) => b.notes - a.notes || a.name.localeCompare(b.name)),
    }))
    .filter((g) => g.members.length > 0);
}

// function(initials)
/** "Jane Q. Doe, MD" → "JD": first and last capitalised words before the credentials. */
export function initials(name: string): string {
  const words = name.replace(/,.*$/, "").split(/\s+/).filter((w) => /^[A-Za-z]/.test(w) && !/\.$/.test(w));
  if (words.length === 0) return "";
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
}

const ORDER_TITLE = /;|\bper (hospital )?(policy|protocol)\b|\border set\b|\bpanel\b|\bprotocol\b/i;
const ORDER_STATUS = /^orders? marked\b|\b(cancel(l?ed)?|held|discontinued)\b.*\b(held|cancel(l?ed)?|orders?)\b/i;

// function(isOrderText)
/** An order set / panel / protocol title, or order status text ("Orders marked Cancel Held …"). */
export function isOrderText(text: string): boolean {
  return ORDER_TITLE.test(text) || ORDER_STATUS.test(text.trim());
}

const clean = (items: string[]) => items.map((s) => s.trim()).filter((s) => s && !isOrderText(s));

// function(cleanCarePlans)
/**
 * Only real care plans: a title that isn't order text, plus at least one goal
 * or activity that isn't order status text. Empty fields are dropped.
 */
export function cleanCarePlans(plans: CarePlanOut[]): CarePlanOut[] {
  return plans.flatMap((plan) => {
    const title = plan.title?.trim();
    if (!title || isOrderText(title)) return [];
    const goals = clean(plan.goals ?? []);
    const activities = clean(plan.activities ?? []);
    if (goals.length === 0 && activities.length === 0) return [];
    const out: CarePlanOut = { id: plan.id, title, goals, activities };
    if (plan.status?.trim()) out.status = plan.status.trim();
    if (plan.date?.trim()) out.date = plan.date.trim();
    if (plan.author?.trim()) out.author = plan.author.trim();
    if (plan.encounterId) out.encounterId = plan.encounterId;
    return [out];
  });
}

